// ============================================================
// ANSICHTSSACHE-AGENT – Kennzahl
// ============================================================
// Eine Zahl mit Beschriftung — der Baustein, aus dem die
// Kopfreihen von Dashboard, Projekt-Detail und Berichten
// bestehen. Vorher stand er dreimal in drei Dateien, jedes Mal
// eine Spur anders.
//
// Zwei Entscheidungen stecken darin:
//
//   `numeric` statt `tabular-nums`: die Zahl bleibt in der
//   Hausschrift und wechselt nicht in die Monospace, behaelt
//   aber gleiche Ziffernbreiten — vier Kacheln nebeneinander
//   springen sonst beim Aktualisieren.
//
//   Der Ton ist NEUTRAL, solange nichts zu melden ist. Farbe
//   kostet Aufmerksamkeit; wer sie an den Normalfall vergibt,
//   hat keine mehr uebrig fuer den Ausnahmefall.
// ============================================================

import Link from "next/link";
import type { LucideIcon } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

export type KennzahlTon = "neutral" | "warnung" | "gefahr";

interface KennzahlProps {
  titel: string;
  wert: string;
  unterzeile?: string;
  icon?: LucideIcon;
  /** Macht die ganze Kachel anklickbar. */
  href?: string;
  ton?: KennzahlTon;
  /** 0–100. Zeichnet einen Balken unter die Zahl. */
  fortschritt?: number;
}

const TON_SCHRIFT: Record<KennzahlTon, string> = {
  neutral: "text-text-primary",
  warnung: "text-warning",
  gefahr: "text-danger",
};

const TON_BALKEN: Record<KennzahlTon, string> = {
  neutral: "bg-text-tertiary",
  warnung: "bg-warning",
  gefahr: "bg-danger",
};

export function Kennzahl({
  titel,
  wert,
  unterzeile,
  icon: Icon,
  href,
  ton = "neutral",
  fortschritt,
}: KennzahlProps) {
  const inhalt = (
    <Card interactive={Boolean(href)} className="h-full">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <p className="text-overline text-text-tertiary">{titel}</p>
          {Icon && (
            // Das Symbol sitzt in einer eigenen, sehr blassen
            // Flaeche. Frei stehend konkurriert es mit der Zahl;
            // gefasst wird es zur Marke der Kachel.
            <span className="bg-bg-tertiary text-text-tertiary grid h-8 w-8 shrink-0 place-items-center rounded-lg">
              <Icon className="h-4 w-4" aria-hidden />
            </span>
          )}
        </div>

        <p className={cn("numeric text-display-lg mt-3", TON_SCHRIFT[ton])}>{wert}</p>

        {fortschritt !== undefined && (
          <div className="bg-bg-tertiary mt-2.5 h-1.5 w-full overflow-hidden rounded-full">
            <div
              className={cn("h-full rounded-full", TON_BALKEN[ton])}
              style={{ width: `${Math.min(Math.max(fortschritt, 0), 100)}%` }}
            />
          </div>
        )}

        {unterzeile && <p className="text-caption text-text-tertiary mt-1.5">{unterzeile}</p>}
      </CardContent>
    </Card>
  );

  return href ? (
    <Link href={href} className="block h-full">
      {inhalt}
    </Link>
  ) : (
    inhalt
  );
}

// ── Die schmale Fassung ─────────────────────────────────────
// Dieselben Zahlen in EINER Zeile. Gebraucht auf den Registern
// eines Projekts (Zeit, Kosten): dort beantworten sie eine
// Frage, die man im Vorbeigehen stellt („liegen wir im
// Rahmen?“) — dafuer brauchen sie keine 150 Pixel Hoehe, und den
// Platz bekommt die Arbeit darunter.
//
// Die Form stammt von der Projektuebersicht, die ihre Kacheln
// als erste gegen eine Leiste getauscht hat. Sie steht hier,
// damit die naechste Seite sie NIMMT statt sie abzuschreiben —
// zwei Fassungen desselben Gedankens laufen immer auseinander.

export type StatStripProgress = {
  /** 0–100 und darueber; ueber 100 wird der Balken rot. */
  prozent: number;
  /** Die Erklaerung beim Zeigen — was gegen was gemessen wird. */
  titel?: string;
  /** Rechts neben dem Balken. Vorgabe ist der Prozentwert. */
  text: string;
};

export function StatStrip({
  fortschritt,
  children,
}: {
  fortschritt?: StatStripProgress;
  children: React.ReactNode;
}) {
  const kritisch = fortschritt !== undefined && fortschritt.prozent > 100;
  const knapp = fortschritt !== undefined && fortschritt.prozent > 85;

  return (
    <Card>
      <CardContent className="flex flex-wrap items-center gap-x-8 gap-y-3 px-5 py-3.5">
        {children}

        {fortschritt && (
          <div
            className="ml-auto flex min-w-[180px] flex-1 items-center gap-3"
            title={fortschritt.titel}
          >
            <div className="bg-bg-tertiary h-1.5 min-w-[80px] flex-1 overflow-hidden rounded-full">
              <div
                className={cn(
                  "h-full rounded-full",
                  kritisch ? "bg-danger" : knapp ? "bg-warning" : "bg-text-tertiary",
                )}
                style={{ width: `${Math.min(Math.max(fortschritt.prozent, 0), 100)}%` }}
              />
            </div>
            <span
              className={cn(
                "numeric text-body-sm shrink-0 font-medium",
                kritisch ? "text-danger" : knapp ? "text-warning" : "text-text-secondary",
              )}
            >
              {fortschritt.text}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Ein Wert in der Leiste: Beschriftung, Zahl, leiser Zusatz.
 *
 * `betont` hebt die Zahl, nach der man die Leiste eigentlich
 * fragt (im Register Zeit: was offen ist und was es bringt) —
 * ueber die Groesse, nicht ueber Farbe: die bleibt fuer den
 * Ausnahmefall.
 */
export function StatStripValue({
  titel,
  wert,
  zusatz,
  ton = "neutral",
  betont = false,
}: {
  titel: string;
  wert: string;
  zusatz?: string;
  ton?: KennzahlTon;
  betont?: boolean;
}) {
  return (
    <div className="min-w-0">
      <p className="text-caption text-text-tertiary">{titel}</p>
      <p
        className={cn(
          "numeric",
          betont ? "text-heading-md" : "text-body-md font-medium",
          TON_SCHRIFT[ton],
        )}
      >
        {wert}
        {zusatz && <span className="text-caption text-text-tertiary font-normal"> {zusatz}</span>}
      </p>
    </div>
  );
}

/**
 * Die leiseren Werte neben betonten: rechts in der Leiste, an
 * derselben Stelle wie der Fortschrittsbalken. Direkt neben einer
 * grossen Zahl stuenden ihre Beschriftungen sonst versetzt.
 */
export function StatStripAside({ children }: { children: React.ReactNode }) {
  return <div className="ml-auto flex flex-wrap items-center gap-x-8 gap-y-3">{children}</div>;
}

/** Die Reihe darum — damit alle Kopfreihen gleich atmen. */
export function KennzahlReihe({
  spalten = 4,
  children,
}: {
  spalten?: 3 | 4 | 5;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "grid grid-cols-1 gap-4 sm:grid-cols-2",
        spalten === 3 && "lg:grid-cols-3",
        spalten === 4 && "lg:grid-cols-4",
        spalten === 5 && "lg:grid-cols-5",
      )}
    >
      {children}
    </div>
  );
}
